import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInwardData } from "../Redux/GeminiOCRSlice/GeminiOCR";
import WpDynamicTable from "../Components/WhatsappTable/WpDynamicTable";
import Loader from "../Components/Loader/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const InwardLogs = () => {
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const dispatch = useDispatch();
  const { InwardData, loading } = useSelector((state) => state.GeminiOCR);

  const [clientNameFilter, setClientNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchInwardData());
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  // Check if still loading
  const isDataLoading = loading || !InwardData || isLoading;

  const convertStatus = (status) => {
    if (status === "Success(201)") return "True";
    if (status === "Fail(500)") return "False";
    return status;
  };

  // Transform data with useMemo
  const transformedData = useMemo(() => {
    if (!InwardData) return [];
    return InwardData.map((el, index) => {
      const newStatus = convertStatus(el?.Status);

      return {
        id: index + 1,
        MainId: el._id,
        WhaClientName: el?.ClientID?.WhaClientName?.trim() || "--",
        PartyName: el?.PartyName || "--",
        ChallanNo: el?.ChallanNo || "--",
        StaticIP: el?.StaticIP || "--",
        Status: newStatus,
        Message: el?.Message || "--",
        BalApiCount: el?.BalApiCount || "--",
        BalAmount: el?.BalAmount || "--",
        Price: el?.Price || "--",
        Time: el?.Time || "--",
      };
    });
  }, [InwardData]);

  // Get all unique client names for the filter dropdown
  const clientNames = useMemo(() => {
    if (!InwardData) return [];
    const names = InwardData
      .map(el => el?.ClientID?.WhaClientName)
      .filter(name => name && name.trim() !== "")
      .map(name => name.trim());
    return [...new Set(names)].sort();
  }, [InwardData]);

  // Filter rows based on selected filters
  const filteredRows = useMemo(() => {
    if (!transformedData.length) return [];

    return transformedData.filter((row) => {
      // Client name filter (case-insensitive)
      const clientMatch = clientNameFilter === "" ||
        row.WhaClientName.toLowerCase() === clientNameFilter.toLowerCase();

      // Status filter
      const statusMatch = statusFilter === "" ||
        row.Status === statusFilter;

      return clientMatch && statusMatch;
    });
  }, [transformedData, clientNameFilter, statusFilter]);

  // Calculate statistics
  const totalRecords = transformedData?.length || 0;
  const filteredRecords = filteredRows?.length || 0;
  const trueStatusCount = filteredRows?.filter(row => row.Status === "True")?.length || 0;
  const falseStatusCount = filteredRows?.filter(row => row.Status === "False")?.length || 0;

  const columns = [
    { field: "id", headerName: "No", flex: 1 },
    { field: "WhaClientName", headerName: "Client Name", flex: 1 },
    { field: "PartyName", headerName: "Party Name", flex: 1 },
    { field: "StaticIP", headerName: "Static IP", flex: 1 },
    { field: "ChallanNo", headerName: "Challan No", flex: 1 },
    { field: "Status", headerName: "Status", flex: 1 },
    { field: "Message", headerName: "Message", flex: 1 },
    { field: "Time", headerName: "Date-Time", flex: 1 },
    { field: "BalAmount", headerName: "Bal. Amount", flex: 1 },
    { field: "BalApiCount", headerName: "Bal. ApiCount", flex: 1 },
    { field: "Price", headerName: "Price", flex: 1 },
  ];

  // Show loader during initial load or when data is not yet available
  if (isDataLoading) {
    return <Loader />;
  }

  // Excel Download Function
  const downloadExcel = () => {
    if (!filteredRows || filteredRows.length === 0) {
      alert("No data to download!");
      return;
    }

    const dataToExport = filteredRows.map((row, idx) => ({
      "No": row.id || idx + 1,
      "Client Name": row.WhaClientName || "-",
      "Party Name": row.PartyName || "-",
      "Challan No": row.ChallanNo || "-",
      "Static IP": row.StaticIP || "-",
      "Status": row.Status || "-",
      "Message": row.Message || "-",
      "Date-Time": row.Time || "-",
      "Bal. Amount": row.BalAmount || "-",
      "Bal. ApiCount": row.BalApiCount || "-",
      "Price": row.Price || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Auto column widths
    worksheet["!cols"] = Object.keys(dataToExport[0] || {}).map((key) => {
      const maxLen = Math.max(
        key.length,
        ...dataToExport.slice(0, 50).map((r) => String(r[key] || "").length)
      );
      return { wch: Math.min(Math.max(maxLen + 3, 10), 40) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "InwardLogs");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "InwardLogReport.xlsx");
  };

  return (
    <>
      <div className="">
        <div className="mb-4 mt-3 flex flex-col md:flex-row gap-4">
          {/* Client Name Filter */}
          <div className="w-full md:w-60">
            <select
              value={clientNameFilter}
              onChange={(e) => setClientNameFilter(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Client Names</option>
              {clientNames.map((name, idx) => (
                <option key={idx} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="w-full md:w-60">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="True">True</option>
              <option value="False">False</option>
            </select>
          </div>
        </div>

        {/*  Table Length + Excel Download Button */}
        <div className="bg-white shadow-md rounded-lg p-2 border border-gray-200">
          {/* Stats Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">

            <div className="flex flex-wrap gap-2">
              <p className="font-semibold text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                Total: {totalRecords}
              </p>
              <p className="font-semibold text-sm text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                Filtered: {filteredRecords}
              </p>
              <p className="font-semibold text-sm text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                True: {trueStatusCount}
              </p>
              <p className="font-semibold text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
                False: {falseStatusCount}
              </p>
            </div>

            {/* Download Button */}
            <button
              onClick={downloadExcel}
              disabled={filteredRows.length === 0}
              className={`px-3 py-1.5 rounded-lg py-2 px-4 hover:shadow transition-all duration-200 flex items-center justify-center gap-2 w-full sm:w-auto text-sm font-medium
${filteredRows.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#122A47] text-white hover:bg-[#0c2545] shadow-sm"
                }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Excel
            </button>

          </div>
        </div>
        {/* Table */}
        <div className="bg-transparent rounded-lg overflow-x-auto mt-5">
          <WpDynamicTable
            columns={columns}
            rows={filteredRows}
            height="450px"
          />
        </div>
      </div>
    </>
  );
};

export default InwardLogs;